import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const SUPABASE_URL     = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const round2 = (n) => Math.round(((n || 0) + Number.EPSILON) * 100) / 100;

export default function PublicInvoicePage() {
  const { id } = useParams();
  const [state, setState] = useState('loading'); // loading | ok | error
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    fetch(`${SUPABASE_URL}/functions/v1/public-invoice?id=${id}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
    })
      .then(r => r.json())
      .then(res => {
        if (res.error || !res.data) { setState('error'); return; }
        setInvoice(res.data);
        setState('ok');
      })
      .catch(() => setState('error'));
  }, [id]);

  if (state === 'loading') return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={{ color: '#888', marginTop: 16 }}>Loading invoice…</p>
    </div>
  );

  if (state === 'error') return (
    <div style={styles.center}>
      <p style={{ color: '#dc2626', fontWeight: 700, fontSize: 18 }}>Invoice not found</p>
      <p style={{ color: '#888', marginTop: 8 }}>This link may be invalid or expired.</p>
    </div>
  );

  const { order, restaurant, billing } = invoice;
  const curr     = restaurant?.default_currency || '₹';
  const fmt      = (n) => `${curr}${round2(n).toFixed(2)}`;
  const gstin    = billing?.gstin || '';
  const isGst    = !!(gstin && (order.gst_amount > 0 || order.applied_gst_percent > 0));
  const docTitle = isGst ? 'TAX INVOICE' : 'RECEIPT';
  const halfRate = round2((order.applied_gst_percent || 0) / 2);
  const cgst     = round2((order.gst_amount || 0) / 2);
  const sgst     = round2((order.gst_amount || 0) / 2);
  const gross    = round2((order.subtotal || 0) + (order.discount_amount || 0));
  const showTaxableRow = ((order.discount_amount || 0) > 0 || (order.gst_amount || 0) > 0 || (order.service_charge_amount || 0) > 0);

  const dateObj = new Date(order.created_at);
  const dateStr = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={styles.page}>
      <div style={styles.sheet}>

        {/* Header */}
        <div style={{ ...styles.section, textAlign: 'center', borderBottom: '2px solid #111' }}>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: 2, marginBottom: 6 }}>{docTitle}</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{restaurant?.name}</div>
          {restaurant?.address && <div style={styles.meta}>{restaurant.address}</div>}
          {restaurant?.contact_phone && <div style={styles.meta}>Ph: {restaurant.contact_phone}</div>}
          {isGst && gstin && <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>GSTIN: {gstin}</div>}
        </div>

        {/* Meta */}
        <div style={styles.section}>
          <div style={styles.metaRow}><span style={styles.label}>Invoice No</span><span style={{ fontWeight: 700 }}>{order.invoice_number || '—'}</span></div>
          {order.order_number && <div style={styles.metaRow}><span style={styles.label}>Order No</span><span style={{ fontWeight: 700 }}>{order.order_number}</span></div>}
          <div style={styles.metaRow}><span style={styles.label}>Date & Time</span><span>{dateStr}, {timeStr}</span></div>
          <div style={styles.metaRow}><span style={styles.label}>Order Type</span><span>{order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Dine-In'}</span></div>
          {order.order_type !== 'TAKEAWAY' && order.restaurant_tables && (
            <div style={styles.metaRow}>
              <span style={styles.label}>Table</span>
              <span>{order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`}</span>
            </div>
          )}
          {order.customer_name && <div style={styles.metaRow}><span style={styles.label}>Customer</span><span>{order.customer_name}</span></div>}
        </div>

        {/* Items */}
        <div style={styles.section}>
          <div style={{ display: 'flex', fontSize: 11, fontWeight: 700, color: '#555', paddingBottom: 6, borderBottom: '1px solid #ddd', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ flex: 1 }}>Item</span>
            <span style={{ width: 36, textAlign: 'right', flexShrink: 0 }}>Qty</span>
            <span style={{ width: 84, textAlign: 'right', flexShrink: 0 }}>Rate</span>
            <span style={{ width: 90, textAlign: 'right', flexShrink: 0 }}>Amt</span>
          </div>
          {(order.order_items || []).map((oi, idx) => (
            <div key={oi.id || idx} style={{ display: 'flex', padding: '3px 0', fontSize: 12.5 }}>
              <span style={{ flex: 1, minWidth: 0, paddingRight: 6, wordBreak: 'break-word' }}>{oi.menu_items?.name || 'Item'}</span>
              <span style={{ width: 36, textAlign: 'right', flexShrink: 0 }}>{oi.quantity}</span>
              <span style={{ width: 84, textAlign: 'right', flexShrink: 0 }}>{fmt(oi.unit_price)}</span>
              <span style={{ width: 90, textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>{fmt(oi.unit_price * oi.quantity)}</span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div style={styles.section}>
          <div style={styles.row}><span>Subtotal</span><span>{fmt(gross)}</span></div>
          {(order.discount_amount || 0) > 0 && (
            <div style={{ ...styles.row, color: '#16a34a' }}>
              <span>Discount</span><span>−{fmt(order.discount_amount)}</span>
            </div>
          )}
          {showTaxableRow && (
            <div style={{ ...styles.row, color: '#555', fontSize: 12 }}>
              <span>Taxable Amount</span><span>{fmt(order.subtotal)}</span>
            </div>
          )}
          {isGst && (
            <>
              <div style={styles.row}><span>CGST @ {halfRate}%</span><span>{fmt(cgst)}</span></div>
              <div style={styles.row}><span>SGST @ {halfRate}%</span><span>{fmt(sgst)}</span></div>
            </>
          )}
          {(order.service_charge_amount || 0) > 0 && (
            <div style={styles.row}>
              <span>Service Charge @ {order.applied_service_charge_percent || ''}%</span>
              <span>{fmt(order.service_charge_amount)}</span>
            </div>
          )}
          <div style={styles.totalRow}>
            <span>TOTAL PAYABLE</span><span>{fmt(order.total_payable)}</span>
          </div>
        </div>

        {/* Payment */}
        <div style={styles.section}>
          {order.paid_at ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Payment: <strong>{(order.payment_method || '').toUpperCase()}</strong></span>
              <span style={styles.badgePaid}>PAID</span>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Payment pending</span>
              <span style={styles.badgeUnpaid}>UNPAID</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ ...styles.section, textAlign: 'center', borderBottom: 'none', color: '#555', fontSize: 11.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Thank you for dining with us!</div>
          <div style={{ color: '#aaa', fontSize: 10 }}>System generated document · {docTitle}</div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', background: '#f3f4f6',
    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    padding: '24px 16px',
  },
  sheet: {
    background: 'white', width: '100%', maxWidth: 420,
    borderRadius: 8, fontFamily: "'Courier New', Courier, monospace",
    fontSize: 13, color: '#111', lineHeight: 1.5,
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
  },
  section: { padding: '14px 18px', borderBottom: '1px dashed #ccc' },
  meta:    { fontSize: 11, color: '#444', marginTop: 2 },
  metaRow: { display: 'flex', gap: 6, padding: '2px 0', fontSize: 12, color: '#333' },
  label:   { color: '#555', minWidth: 110 },
  row:     { display: 'flex', justifyContent: 'space-between', padding: '2px 0' },
  totalRow: {
    display: 'flex', justifyContent: 'space-between',
    padding: '8px 0 4px', borderTop: '2px solid #111',
    fontWeight: 800, fontSize: 15, marginTop: 4,
  },
  badgePaid:   { background: '#16a34a', color: 'white', padding: '2px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700 },
  badgeUnpaid: { background: '#dc2626', color: 'white', padding: '2px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700 },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  spinner: {
    width: 36, height: 36, borderRadius: '50%',
    border: '3px solid #e5e7eb', borderTopColor: '#25d366',
    animation: 'spin 0.8s linear infinite',
  },
};
